function assertFinitePoint(point, label) {
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new TypeError(`${label} must contain finite x and y values`);
  }
}

function assertViewport(viewport) {
  if (!viewport || !Number.isFinite(viewport.scale) || viewport.scale <= 0) {
    throw new TypeError('viewport scale must be a positive finite number');
  }
  assertFinitePoint(viewport.offset, 'viewport offset');
}

export function screenToWorld(screenPoint, containerRect, viewport) {
  assertFinitePoint(screenPoint, 'screen point');
  assertViewport(viewport);

  return viewportToWorld(clientToViewport(screenPoint, containerRect), viewport);
}

export function clientToViewport(clientPoint, containerRect) {
  assertFinitePoint(clientPoint, 'client point');

  const left = Number.isFinite(containerRect?.left) ? containerRect.left : 0;
  const top = Number.isFinite(containerRect?.top) ? containerRect.top : 0;

  return {
    x: clientPoint.x - left,
    y: clientPoint.y - top,
  };
}

export function viewportToWorld(viewportPoint, viewport) {
  assertFinitePoint(viewportPoint, 'viewport point');
  assertViewport(viewport);
  return {
    x: (viewportPoint.x - viewport.offset.x) / viewport.scale,
    y: (viewportPoint.y - viewport.offset.y) / viewport.scale,
  };
}

export function worldToViewport(worldPoint, viewport) {
  assertFinitePoint(worldPoint, 'world point');
  assertViewport(viewport);
  return {
    x: viewport.offset.x + worldPoint.x * viewport.scale,
    y: viewport.offset.y + worldPoint.y * viewport.scale,
  };
}

export function worldToScreen(worldPoint, containerRect, viewport) {
  assertFinitePoint(worldPoint, 'world point');
  assertViewport(viewport);

  const left = Number.isFinite(containerRect?.left) ? containerRect.left : 0;
  const top = Number.isFinite(containerRect?.top) ? containerRect.top : 0;
  const viewportPoint = worldToViewport(worldPoint, viewport);

  return {
    x: left + viewportPoint.x,
    y: top + viewportPoint.y,
  };
}

export function viewportRectToWorldRect(rect, viewport) {
  if (
    !rect
    || !Number.isFinite(rect.x)
    || !Number.isFinite(rect.y)
    || !Number.isFinite(rect.width)
    || !Number.isFinite(rect.height)
  ) {
    throw new TypeError('viewport rect must contain finite x, y, width, and height');
  }
  const topLeft = viewportToWorld({ x: rect.x, y: rect.y }, viewport);
  const bottomRight = viewportToWorld({
    x: rect.x + Math.max(0, rect.width),
    y: rect.y + Math.max(0, rect.height),
  }, viewport);
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y,
  };
}

export function createWorldViewportConstraint({
  viewportSize,
  viewportOrigin = { x: 0, y: 0 },
  viewport,
  inset = 12,
  obstacles = [],
} = {}) {
  const width = Number.isFinite(viewportSize?.width) ? Math.max(0, viewportSize.width) : 0;
  const height = Number.isFinite(viewportSize?.height) ? Math.max(0, viewportSize.height) : 0;
  const safeInset = Math.max(0, Number.isFinite(inset) ? inset : 12);
  const originX = Number.isFinite(viewportOrigin?.x) ? viewportOrigin.x : 0;
  const originY = Number.isFinite(viewportOrigin?.y) ? viewportOrigin.y : 0;
  const viewportBounds = {
    x: originX + Math.min(safeInset, width / 2),
    y: originY + Math.min(safeInset, height / 2),
    width: Math.max(0, width - safeInset * 2),
    height: Math.max(0, height - safeInset * 2),
  };
  const worldBounds = viewportRectToWorldRect(viewportBounds, viewport);

  return {
    bounds: {
      minX: worldBounds.x,
      minY: worldBounds.y,
      maxX: worldBounds.x + worldBounds.width,
      maxY: worldBounds.y + worldBounds.height,
    },
    obstacles: (Array.isArray(obstacles) ? obstacles : []).map(
      (rect) => viewportRectToWorldRect(rect, viewport)
    ),
  };
}

function rectsOverlap(left, right) {
  return !(
    left.x + left.width <= right.x
    || right.x + right.width <= left.x
    || left.y + left.height <= right.y
    || right.y + right.height <= left.y
  );
}

function rectFitsBounds(rect, bounds) {
  return rect.x >= bounds.x
    && rect.y >= bounds.y
    && rect.x + rect.width <= bounds.x + bounds.width
    && rect.y + rect.height <= bounds.y + bounds.height;
}

export function minimumPanDeltaToReveal({
  rect,
  bounds,
  obstacles = [],
} = {}) {
  if (
    !rect
    || !bounds
    || ![rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)
    || ![bounds.x, bounds.y, bounds.width, bounds.height].every(Number.isFinite)
  ) {
    throw new TypeError('rect and bounds must contain finite x, y, width, and height');
  }

  const target = {
    x: rect.width <= bounds.width
      ? Math.min(
        Math.max(rect.x, bounds.x),
        bounds.x + bounds.width - rect.width
      )
      : bounds.x + (bounds.width - rect.width) / 2,
    y: rect.height <= bounds.height
      ? Math.min(
        Math.max(rect.y, bounds.y),
        bounds.y + bounds.height - rect.height
      )
      : bounds.y + (bounds.height - rect.height) / 2,
  };
  const safeObstacles = Array.isArray(obstacles) ? obstacles : [];
  const candidateXs = new Set([
    target.x,
    bounds.x,
    bounds.x + bounds.width - rect.width,
  ]);
  const candidateYs = new Set([
    target.y,
    bounds.y,
    bounds.y + bounds.height - rect.height,
  ]);
  safeObstacles.forEach((obstacle) => {
    candidateXs.add(obstacle.x - rect.width);
    candidateXs.add(obstacle.x + obstacle.width);
    candidateYs.add(obstacle.y - rect.height);
    candidateYs.add(obstacle.y + obstacle.height);
  });

  const candidates = [...candidateXs].flatMap((x) => (
    [...candidateYs].map((y) => ({ x, y, width: rect.width, height: rect.height }))
  ))
    .filter((candidate) => (
      rectFitsBounds(candidate, bounds)
      && !safeObstacles.some((obstacle) => rectsOverlap(candidate, obstacle))
    ))
    .sort((left, right) => (
      Math.hypot(left.x - rect.x, left.y - rect.y)
      - Math.hypot(right.x - rect.x, right.y - rect.y)
      || left.y - right.y
      || left.x - right.x
    ));

  const destination = candidates[0] || { ...rect, ...target };
  return {
    x: destination.x - rect.x,
    y: destination.y - rect.y,
  };
}

export function clampViewportOverlay(point, viewportSize, overlaySize, padding = 8) {
  const safePadding = Math.max(0, Number.isFinite(padding) ? padding : 8);
  const viewportWidth = Math.max(0, Number.isFinite(viewportSize?.width) ? viewportSize.width : 0);
  const viewportHeight = Math.max(0, Number.isFinite(viewportSize?.height) ? viewportSize.height : 0);
  const overlayWidth = Math.max(0, Number.isFinite(overlaySize?.width) ? overlaySize.width : 0);
  const overlayHeight = Math.max(0, Number.isFinite(overlaySize?.height) ? overlaySize.height : 0);
  const minX = safePadding;
  const minY = safePadding;
  const maxX = Math.max(minX, viewportWidth - overlayWidth - safePadding);
  const maxY = Math.max(minY, viewportHeight - overlayHeight - safePadding);

  return {
    x: Math.min(Math.max(Number.isFinite(point?.x) ? point.x : minX, minX), maxX),
    y: Math.min(Math.max(Number.isFinite(point?.y) ? point.y : minY, minY), maxY),
  };
}

export function getPointDistance(first, second) {
  assertFinitePoint(first, 'first point');
  assertFinitePoint(second, 'second point');
  return Math.hypot(second.x - first.x, second.y - first.y);
}

export function getPointMidpoint(first, second) {
  assertFinitePoint(first, 'first point');
  assertFinitePoint(second, 'second point');
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
  };
}

export function zoomViewportAtPoint(viewport, newScale, anchorPoint) {
  assertViewport(viewport);
  assertFinitePoint(anchorPoint, 'zoom anchor');
  if (!Number.isFinite(newScale) || newScale <= 0) {
    throw new TypeError('new scale must be a positive finite number');
  }
  const worldAnchor = {
    x: (anchorPoint.x - viewport.offset.x) / viewport.scale,
    y: (anchorPoint.y - viewport.offset.y) / viewport.scale,
  };
  return {
    scale: newScale,
    offset: {
      x: anchorPoint.x - worldAnchor.x * newScale,
      y: anchorPoint.y - worldAnchor.y * newScale,
    },
  };
}

export function calculatePinchViewport({
  initialViewport,
  initialPoints,
  currentPoints,
  minScale = 0.2,
  maxScale = 3,
}) {
  assertViewport(initialViewport);
  if (!Array.isArray(initialPoints) || initialPoints.length !== 2) {
    throw new TypeError('initial pinch requires exactly two points');
  }
  if (!Array.isArray(currentPoints) || currentPoints.length !== 2) {
    throw new TypeError('current pinch requires exactly two points');
  }
  const initialDistance = getPointDistance(initialPoints[0], initialPoints[1]);
  if (initialDistance <= 0) return initialViewport;
  const currentDistance = getPointDistance(currentPoints[0], currentPoints[1]);
  const requestedScale = initialViewport.scale * (currentDistance / initialDistance);
  const scale = Math.min(maxScale, Math.max(minScale, requestedScale));
  const initialMidpoint = getPointMidpoint(initialPoints[0], initialPoints[1]);
  const currentMidpoint = getPointMidpoint(currentPoints[0], currentPoints[1]);
  const worldAnchor = {
    x: (initialMidpoint.x - initialViewport.offset.x) / initialViewport.scale,
    y: (initialMidpoint.y - initialViewport.offset.y) / initialViewport.scale,
  };
  return {
    scale,
    offset: {
      x: currentMidpoint.x - worldAnchor.x * scale,
      y: currentMidpoint.y - worldAnchor.y * scale,
    },
  };
}

export function fitBoundsToViewport(bounds, viewportSize, options = {}) {
  const { width, height } = viewportSize || {};
  if (!bounds || ![bounds.minX, bounds.minY, bounds.maxX, bounds.maxY].every(Number.isFinite)) {
    throw new TypeError('bounds must contain finite min/max values');
  }
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    throw new TypeError('viewport size must contain positive width and height');
  }

  const padding = Number.isFinite(options.padding) ? Math.max(0, options.padding) : 80;
  const minScale = Number.isFinite(options.minScale) ? options.minScale : 0.2;
  const maxScale = Number.isFinite(options.maxScale) ? options.maxScale : 1.5;
  const boundsWidth = Math.max(1, bounds.maxX - bounds.minX);
  const boundsHeight = Math.max(1, bounds.maxY - bounds.minY);
  const availableWidth = Math.max(1, width - padding * 2);
  const availableHeight = Math.max(1, height - padding * 2);
  const scale = Math.min(
    maxScale,
    Math.max(minScale, Math.min(availableWidth / boundsWidth, availableHeight / boundsHeight))
  );
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return {
    scale,
    offset: {
      x: width / 2 - centerX * scale,
      y: height / 2 - centerY * scale,
    },
  };
}
